import { useEffect, useRef } from "react";
import { trackOnce } from "../track";
import quotes from "./reviews.json";
import type { Language } from "../types";
import type { Words } from "./copy";

/** What other buyers said, in their own words, beside the price.
 *
 *  The trust pill says "5.0 on Etsy" and nothing else, and a number with no
 *  words behind it is easy to scroll past. These are the three Etsy reviews
 *  the website quotes (reviews.json, copied from it word for word, by-lines
 *  included), shown here because the page is where the money is decided and
 *  the person deciding has not seen the website's reviews section: most come
 *  straight from the form, or from a mail.
 *
 *  Nothing is counted or invented here: the rating and its count are the
 *  website's (data/business.json etsyRating), and the one link goes to Etsy's
 *  own list, where they can read every review there is. */
export default function Reviews({ language, words: t }: { language: Language; words: Words }) {
  const ref = useRef<HTMLDivElement>(null);
  const table = quotes as unknown as Record<string, { id: string; text: string; by: string }[]>;
  const list = (Array.isArray(table[language]) && table[language]!.length ? table[language] : table.en) ?? [];

  // Whether anyone gets this far, counted the same way the price and the
  // proof are. Nothing on the lead changes for it; it is a number for later.
  useEffect(() => {
    const node = ref.current;
    if (!node || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) trackOnce("section.seen", { id: "reviews" });
      },
      { threshold: 0.5 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (!list.length) return null;

  return (
    <div className="card reviews" ref={ref}>
      <h2>{t.t("reviewsTitle")}</h2>
      <ul className="reviews-list">
        {list.map((r) => (
          <li className="review" key={r.id}>
            <span className="review-stars" role="img" aria-label={t.t("reviewsStars")}>
              ★★★★★
            </span>
            <blockquote className="review-text">{r.text}</blockquote>
            <span className="review-by tiny muted">{r.by}</span>
          </li>
        ))}
      </ul>
      <a
        className="review-more tiny"
        href="https://www.etsy.com/shop/MiniMeWonder#reviews"
        target="_blank"
        rel="noopener"
      >
        {t.t("reviewsEtsy")}
      </a>
    </div>
  );
}
